import Hero from '@global/Blocks/Hero'
import Text from '@global/Blocks/Text'
import TwinEqualCTA from '@global/Blocks/TwinEqualCTA'
import WagtailBlocksSection from '@global/Blocks/WagtailBlocksSection'
import Breadcrumbs from '@global/Breadcrumbs'
import LoadmoreInfo from '@global/SearchResults/LoadmoreInfo'
import { getChildPages } from '@lib/api-services/cmsService'
import { apiFieldsQuery, defaultImages } from '@lib/variables'
import { Section } from '@styles/global.styled'
import { NextSeo } from 'next-seo'
import { useState } from 'react'

const alignmentMap = {
  left: 'L',
  center: 'C',
  right: 'R',
}

const RegionPage = ({
  suburbsData,
  pageData: {
    id,
    title,
    intro_text,
    meta,
    content_blocks,
    hero_image,
    text_position,
    heading,
  },
}) => {
  const [suburbs, setSuburbs] = useState({
    ...suburbsData,
  })
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 12,
  })

  const getSuburbs = async (params) => {
    const page = params.page || 1
    const limit = params.limit || 12
    const offset = (page - 1) * limit

    let nQueryParams = { ...params, page, limit, offset }
    setQueryParams({ ...nQueryParams })

    const resp = await getChildPages(
      'suburbs.SuburbPage',
      id,
      null,
      null,
      nQueryParams,
      apiFieldsQuery.SuburbPage
    )

    const data = {
      ...resp,
      items: resp.items
        ? resp.items.map((item) => ({
            description: item.preamble,
            title: item.title,
            image: item?.featured_image_thumb?.url,
            url: item.meta.path,
          }))
        : [],
    }

    return data
  }

  const defaultTitle = `${title} Property Market & Suburb Profiles | Barry Plant`
  const defaultDescription = `Explore detailed suburb profiles, up-to-date median prices, sales data, and rental market and investor insights for ${title} suburbs.`
  const metaTitle = meta.meta_title || defaultTitle
  const metaDescription = meta.meta_description || defaultDescription || ''
  const ogTitle = meta.og_title || metaTitle
  const ogImage = meta.og_img
    ? [{ url: meta.og_img }]
    : hero_image?.url
    ? [{ url: hero_image?.url }]
    : [{ url: defaultImages.mediumNavy }]
  const ogDescription = meta.og_description || metaDescription

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          images: ogImage,
          title: ogTitle,
          description: ogDescription,
          type: 'website',
        }}
      />
      <Hero
        bgImageUrl={hero_image?.url}
        title={title}
        breadcrumbs={
          <Breadcrumbs
            secondary
            breadcrumbs={[
              { label: 'Suburb Profiles', url: '/suburb-profile' },
              { label: meta.parent.title, url: meta.parent_path },
              { label: title, url: '#' },
            ]}
          />
        }
      />
      {!!intro_text && (
        <Section withPaddingBottom>
          <Text alignment={alignmentMap[text_position]} content={intro_text} />
        </Section>
      )}
      <TwinEqualCTA
        title={heading || `SUBURBS IN THE ${title.toUpperCase()}`}
        itemBackground='white'
        items={suburbs.items.map((item) => ({
          ...item,
          description: '',
        }))}
        numberOfItems={3}
        size='sm'
        loadMoreButton={
          <LoadmoreInfo
            noMarginBottom
            total={suburbs.total}
            numberShowing={suburbs?.items?.length || 0}
            queryParams={queryParams}
            apiCall={getSuburbs}
            page={queryParams.page}
            onSuccess={(data) => {
              setSuburbs((prev) => ({
                ...prev,
                items: prev.items.concat(data),
              }))
            }}
          />
        }
      />
      {content_blocks?.length > 0 && (
        <WagtailBlocksSection blocks={content_blocks} />
      )}
    </>
  )
}

RegionPage.getInitialProps = async (ctx, pageData) => {
  let childrenPages = await getChildPages(
    'suburbs.SuburbPage',
    pageData.id,
    null,
    null,
    { limit: 12 },
    apiFieldsQuery.SuburbPage
  )

  return {
    suburbsData: {
      total: childrenPages.meta.total_count,
      items:
        childrenPages.items?.map((item) => {
          return {
            description: item.preamble,
            title: item.title,
            image: item?.featured_image_thumb?.url,
            url: item.meta.path,
          }
        }) ?? [],
    },
  }
}
export default RegionPage
